import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export const billingService = {
  // Get all invoices with optional filters
  getAll: async (filters = {}) => {
    try {
      let query = supabase?.from('invoices')?.select('*, commercial_packages(package_name, tier), production_batches(batch_number)');
      
      if (filters?.search) {
        query = query?.or(`invoice_number.ilike.%${filters?.search}%,client_name.ilike.%${filters?.search}%`);
      }
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.period && filters?.period !== 'all') {
        const now = new Date();
        if (filters?.period === 'this_month') {
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          query = query?.gte('issue_date', startOfMonth?.toISOString()?.split('T')?.[0]);
        } else if (filters?.period === 'last_month') {
          const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
          query = query?.gte('issue_date', startOfLastMonth?.toISOString()?.split('T')?.[0])?.lte('issue_date', endOfLastMonth?.toISOString()?.split('T')?.[0]);
        }
      }
      if (filters?.minAmount) {
        query = query?.gte('total_amount', filters?.minAmount);
      }
      if (filters?.maxAmount) {
        query = query?.lte('total_amount', filters?.maxAmount);
      }
      if (filters?.startDate) {
        query = query?.gte('issue_date', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('issue_date', filters?.endDate);
      }
      
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return data?.map(invoice => ({
        id: invoice?.id,
        invoiceNumber: invoice?.invoice_number,
        clientName: invoice?.client_name,
        clientEmail: invoice?.client_email,
        packageId: invoice?.package_id,
        package: invoice?.commercial_packages,
        batchId: invoice?.batch_id,
        batch: invoice?.production_batches,
        status: invoice?.status,
        issueDate: invoice?.issue_date,
        dueDate: invoice?.due_date,
        paymentDate: invoice?.payment_date,
        subtotal: invoice?.subtotal,
        taxPercentage: invoice?.tax_percentage,
        taxAmount: invoice?.tax_amount,
        totalAmount: invoice?.total_amount,
        amountPaid: invoice?.amount_paid,
        balanceDue: invoice?.balance_due,
        paymentMethod: invoice?.payment_method,
        notes: invoice?.notes,
        createdAt: invoice?.created_at,
        updatedAt: invoice?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  // Get single invoice by ID
  getById: async (id) => {
    try {
      const { data, error } = await supabase?.from('invoices')?.select('*, commercial_packages(package_name, tier, final_price), production_batches(batch_number, patient_name)')?.eq('id', id)?.single();
      
      if (error) throw error;

      const { data: lineRows } = await supabase
        ?.from('invoice_line_items')
        ?.select('*')
        ?.eq('invoice_id', id)
        ?.order('sort_order', { ascending: true });

      const lineItems = (lineRows || []).map((row) => ({
        id: row.id,
        lineType: row.line_type,
        serviceId: row.service_id,
        description: row.description,
        quantity: row.quantity,
        unitPrice: Number(row.unit_price),
        lineTotal: Number(row.line_total),
        amount: Number(row.line_total),
      }));
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        invoiceNumber: data?.invoice_number,
        clientName: data?.client_name,
        clientEmail: data?.client_email,
        packageId: data?.package_id,
        package: data?.commercial_packages,
        batchId: data?.batch_id,
        batch: data?.production_batches,
        status: data?.status,
        issueDate: data?.issue_date,
        dueDate: data?.due_date,
        paymentDate: data?.payment_date,
        subtotal: data?.subtotal,
        taxPercentage: data?.tax_percentage,
        taxAmount: data?.tax_amount,
        totalAmount: data?.total_amount,
        amountPaid: data?.amount_paid,
        balanceDue: data?.balance_due,
        paymentMethod: data?.payment_method,
        notes: data?.notes,
        lineItems,
        line_items: lineItems,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error fetching invoice ${id}:`, error);
      throw error;
    }
  },

  // Create new invoice
  create: async (invoiceData) => {
    try {
      const { data, error } = await supabase?.from('invoices')?.insert({
          invoice_number: invoiceData?.invoiceNumber,
          client_name: invoiceData?.clientName,
          client_email: invoiceData?.clientEmail || null,
          package_id: invoiceData?.packageId || null,
          batch_id: invoiceData?.batchId || null,
          status: invoiceData?.status || 'draft',
          issue_date: invoiceData?.issueDate,
          due_date: invoiceData?.dueDate,
          payment_date: invoiceData?.paymentDate || null,
          subtotal: invoiceData?.subtotal,
          tax_percentage: invoiceData?.taxPercentage || 0,
          tax_amount: invoiceData?.taxAmount || 0,
          total_amount: invoiceData?.totalAmount,
          amount_paid: invoiceData?.amountPaid || 0,
          balance_due: invoiceData?.balanceDue,
          payment_method: invoiceData?.paymentMethod || null,
          notes: invoiceData?.notes || null
        })?.select()?.single();
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        invoiceNumber: data?.invoice_number,
        clientName: data?.client_name,
        clientEmail: data?.client_email,
        packageId: data?.package_id,
        batchId: data?.batch_id,
        status: data?.status,
        issueDate: data?.issue_date,
        dueDate: data?.due_date,
        paymentDate: data?.payment_date,
        subtotal: data?.subtotal,
        taxPercentage: data?.tax_percentage,
        taxAmount: data?.tax_amount,
        totalAmount: data?.total_amount,
        amountPaid: data?.amount_paid,
        balanceDue: data?.balance_due,
        paymentMethod: data?.payment_method,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }
  },

  // Update existing invoice
  update: async (id, invoiceData) => {
    try {
      const updateData = {};
      if (invoiceData?.invoiceNumber !== undefined) updateData.invoice_number = invoiceData?.invoiceNumber;
      if (invoiceData?.clientName !== undefined) updateData.client_name = invoiceData?.clientName;
      if (invoiceData?.clientEmail !== undefined) updateData.client_email = invoiceData?.clientEmail;
      if (invoiceData?.packageId !== undefined) updateData.package_id = invoiceData?.packageId;
      if (invoiceData?.batchId !== undefined) updateData.batch_id = invoiceData?.batchId;
      if (invoiceData?.status !== undefined) updateData.status = invoiceData?.status;
      if (invoiceData?.issueDate !== undefined) updateData.issue_date = invoiceData?.issueDate;
      if (invoiceData?.dueDate !== undefined) updateData.due_date = invoiceData?.dueDate;
      if (invoiceData?.paymentDate !== undefined) updateData.payment_date = invoiceData?.paymentDate;
      if (invoiceData?.subtotal !== undefined) updateData.subtotal = invoiceData?.subtotal;
      if (invoiceData?.taxPercentage !== undefined) updateData.tax_percentage = invoiceData?.taxPercentage;
      if (invoiceData?.taxAmount !== undefined) updateData.tax_amount = invoiceData?.taxAmount;
      if (invoiceData?.totalAmount !== undefined) updateData.total_amount = invoiceData?.totalAmount;
      if (invoiceData?.amountPaid !== undefined) updateData.amount_paid = invoiceData?.amountPaid;
      if (invoiceData?.balanceDue !== undefined) updateData.balance_due = invoiceData?.balanceDue;
      if (invoiceData?.paymentMethod !== undefined) updateData.payment_method = invoiceData?.paymentMethod;
      if (invoiceData?.notes !== undefined) updateData.notes = invoiceData?.notes;
      
      const { data, error } = await supabase?.from('invoices')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      // Convert snake_case to camelCase
      return {
        id: data?.id,
        invoiceNumber: data?.invoice_number,
        clientName: data?.client_name,
        clientEmail: data?.client_email,
        packageId: data?.package_id,
        batchId: data?.batch_id,
        status: data?.status,
        issueDate: data?.issue_date,
        dueDate: data?.due_date,
        paymentDate: data?.payment_date,
        subtotal: data?.subtotal,
        taxPercentage: data?.tax_percentage,
        taxAmount: data?.tax_amount,
        totalAmount: data?.total_amount,
        amountPaid: data?.amount_paid,
        balanceDue: data?.balance_due,
        paymentMethod: data?.payment_method,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating invoice ${id}:`, error);
      throw error;
    }
  },

  // Delete invoice
  delete: async (id) => {
    try {
      const { error } = await supabase?.from('invoices')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error(`Error deleting invoice ${id}:`, error);
      throw error;
    }
  },

  // Update invoice status
  updateStatus: async (id, status) => {
    try {
      const updateData = { status };
      
      // If marking as paid, set payment date to now
      if (status === 'paid') {
        updateData.payment_date = new Date()?.toISOString()?.split('T')?.[0];
        updateData.amount_paid = updateData?.total_amount;
        updateData.balance_due = 0;
      }
      
      const { data, error } = await supabase?.from('invoices')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        status: data?.status,
        paymentDate: data?.payment_date,
        amountPaid: data?.amount_paid,
        balanceDue: data?.balance_due,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error updating invoice status ${id}:`, error);
      throw error;
    }
  },

  // Record payment for invoice
  recordPayment: async (id, paymentData) => {
    try {
      const { data: invoice } = await supabase?.from('invoices')?.select('total_amount, amount_paid')?.eq('id', id)?.single();
      
      const newAmountPaid = parseFloat(invoice?.amount_paid || 0) + parseFloat(paymentData?.amount);
      const newBalanceDue = parseFloat(invoice?.total_amount) - newAmountPaid;
      
      const updateData = {
        amount_paid: newAmountPaid,
        balance_due: newBalanceDue,
        payment_method: paymentData?.paymentMethod,
        payment_date: paymentData?.paymentDate || new Date()?.toISOString()?.split('T')?.[0]
      };
      
      // Update status if fully paid
      if (newBalanceDue <= 0) {
        updateData.status = 'paid';
      }
      
      const { data, error } = await supabase?.from('invoices')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        status: data?.status,
        amountPaid: data?.amount_paid,
        balanceDue: data?.balance_due,
        paymentMethod: data?.payment_method,
        paymentDate: data?.payment_date,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error recording payment for invoice ${id}:`, error);
      throw error;
    }
  },

  // Get financial summary
  getFinancialSummary: async () => {
    try {
      const { data, error } = await supabase?.from('invoices')?.select('status, total_amount, amount_paid, balance_due');
      
      if (error) throw error;
      
      // Calculate aggregate metrics
      const summary = {
        totalRevenue: 0,
        paidAmount: 0,
        pendingAmount: 0,
        overdueAmount: 0,
        invoiceCount: {
          total: data?.length || 0,
          paid: 0,
          pending: 0,
          overdue: 0,
          draft: 0
        }
      };
      
      data?.forEach(invoice => {
        // Safe numeric conversion with fallback to 0
        const totalAmount = parseFloat(invoice?.total_amount);
        const amountPaid = parseFloat(invoice?.amount_paid);
        const balanceDue = parseFloat(invoice?.balance_due);
        
        summary.totalRevenue += isNaN(totalAmount) ? 0 : totalAmount;
        summary.paidAmount += isNaN(amountPaid) ? 0 : amountPaid;
        
        if (invoice?.status === 'paid') {
          summary.invoiceCount.paid++;
        } else if (invoice?.status === 'pending') {
          summary.invoiceCount.pending++;
          summary.pendingAmount += isNaN(balanceDue) ? 0 : balanceDue;
        } else if (invoice?.status === 'overdue') {
          summary.invoiceCount.overdue++;
          summary.overdueAmount += isNaN(balanceDue) ? 0 : balanceDue;
        } else if (invoice?.status === 'draft') {
          summary.invoiceCount.draft++;
        }
      });
      
      return summary;
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      throw error;
    }
  },

  // Generate invoice PDF (placeholder - would need backend implementation)
  generatePDF: async (id) => {
    try {
      const invoiceDetail = await billingService.getById(id);
      const invoice = {
        ...invoiceDetail,
        invoice_number: invoiceDetail.invoiceNumber,
        issue_date: invoiceDetail.issueDate,
        due_date: invoiceDetail.dueDate,
        client_name: invoiceDetail.clientName,
        client_email: invoiceDetail.clientEmail,
        line_items: invoiceDetail.line_items || invoiceDetail.lineItems || [],
      };

      if (!invoice?.id) throw new Error('Invoice not found');

      // Create PDF document
      const doc = new jsPDF();
      
      // Set document properties
      doc?.setProperties({
        title: `Invoice ${invoice?.invoice_number}`,
        subject: 'Invoice',
        author: 'ClearMotion',
        keywords: 'invoice, billing',
        creator: 'ClearMotion Billing System'
      });

      // Add header
      doc?.setFontSize(20);
      doc?.setTextColor(40, 40, 40);
      doc?.text('FACTURE', 105, 20, { align: 'center' });

      // Add invoice number and dates
      doc?.setFontSize(10);
      doc?.setTextColor(100, 100, 100);
      doc?.text(`Numéro: ${invoice?.invoice_number}`, 20, 40);
      doc?.text(`Date d'émission: ${new Date(invoice.issue_date)?.toLocaleDateString('fr-FR')}`, 20, 47);
      doc?.text(`Date d'échéance: ${new Date(invoice.due_date)?.toLocaleDateString('fr-FR')}`, 20, 54);

      // Add status badge
      const statusColors = {
        paid: [34, 197, 94],
        pending: [251, 191, 36],
        overdue: [239, 68, 68],
        cancelled: [156, 163, 175]
      };
      const statusColor = statusColors?.[invoice?.status] || [100, 100, 100];
      doc?.setFillColor(...statusColor);
      doc?.roundedRect(150, 37, 40, 8, 2, 2, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(9);
      const statusText = invoice?.status?.charAt(0)?.toUpperCase() + invoice?.status?.slice(1);
      doc?.text(statusText, 170, 42, { align: 'center' });

      // Add client information
      doc?.setFontSize(11);
      doc?.setTextColor(40, 40, 40);
      doc?.text('Facturé à:', 20, 70);
      doc?.setFontSize(10);
      doc?.setTextColor(60, 60, 60);
      doc?.text(invoice?.client_name || 'N/A', 20, 77);
      if (invoice?.client_email) {
        doc?.text(invoice?.client_email, 20, 84);
      }

      // Add line items table
      const tableData = [];
      if (invoice?.line_items && Array.isArray(invoice?.line_items)) {
        invoice?.line_items?.forEach(item => {
          tableData?.push([
            item?.description || '',
            item?.quantity?.toString() || '1',
            `${Number(item?.unit_price ?? item?.unitPrice ?? 0).toFixed(2)} €`,
            `${Number(item?.amount ?? item?.line_total ?? item?.lineTotal ?? 0).toFixed(2)} €`
          ]);
        });
      }

      doc?.autoTable({
        startY: 95,
        head: [['Description', 'Quantité', 'Prix unitaire', 'Montant']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 9,
          cellPadding: 5
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 30, halign: 'center' },
          2: { cellWidth: 40, halign: 'right' },
          3: { cellWidth: 40, halign: 'right' }
        }
      });

      // Calculate final Y position after table
      const finalY = doc?.lastAutoTable?.finalY + 10;

      // Add totals
      doc?.setFontSize(10);
      doc?.setTextColor(60, 60, 60);
      
      let currentY = finalY;
      
      // Subtotal
      if (invoice?.subtotal) {
        doc?.text('Sous-total:', 130, currentY);
        doc?.text(`${invoice?.subtotal?.toFixed(2)} €`, 190, currentY, { align: 'right' });
        currentY += 7;
      }

      // Tax
      if (invoice?.tax) {
        doc?.text(`TVA (${invoice?.tax_rate || 20}%):`, 130, currentY);
        doc?.text(`${invoice?.tax?.toFixed(2)} €`, 190, currentY, { align: 'right' });
        currentY += 7;
      }

      // Discount
      if (invoice?.discount) {
        doc?.setTextColor(34, 197, 94);
        doc?.text('Remise:', 130, currentY);
        doc?.text(`-${invoice?.discount?.toFixed(2)} €`, 190, currentY, { align: 'right' });
        currentY += 7;
        doc?.setTextColor(60, 60, 60);
      }

      // Total
      doc?.setFontSize(12);
      doc?.setFont(undefined, 'bold');
      doc?.setTextColor(40, 40, 40);
      doc?.text('Total:', 130, currentY + 3);
      doc?.text(`${invoice?.amount?.toFixed(2)} €`, 190, currentY + 3, { align: 'right' });

      // Add notes if present
      if (invoice?.notes) {
        currentY += 20;
        doc?.setFontSize(10);
        doc?.setFont(undefined, 'bold');
        doc?.text('Notes:', 20, currentY);
        doc?.setFont(undefined, 'normal');
        doc?.setTextColor(80, 80, 80);
        const splitNotes = doc?.splitTextToSize(invoice?.notes, 170);
        doc?.text(splitNotes, 20, currentY + 7);
      }

      // Add footer
      const pageHeight = doc?.internal?.pageSize?.height;
      doc?.setFontSize(8);
      doc?.setTextColor(150, 150, 150);
      doc?.text('Généré par ClearMotion Billing System', 105, pageHeight - 10, { align: 'center' });

      // Return PDF as blob
      return doc?.output('blob');
    } catch (error) {
      console.error(`Error generating PDF for invoice ${id}:`, error);
      throw error;
    }
  },

  // Send invoice by email (placeholder - would need backend implementation)
  sendByEmail: async (id, emailData) => {
    try {
      // This would typically call an edge function or backend service
      console.log(`Email sending for invoice ${id} would be implemented via edge function`);
      throw new Error('Email sending requires edge function implementation');
    } catch (error) {
      console.error(`Error sending invoice ${id} by email:`, error);
      throw error;
    }
  },
};

export default billingService;